import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { MAX_QUERY_LENGTH } from "../../shared/filters";
import { formatArs } from "../../shared/money";
import type { Product } from "../../shared/types";
import { useI18n } from "../i18n/I18nProvider";
import { api, imageUrl } from "../lib/api";
import { useCatalog, useStoreSales } from "../lib/CatalogProvider";
import { Price } from "./Price";
import { useGame } from "../lib/GameProvider";

const MIN_CHARS = 2;

/** The name without the trailing " — Set" and "(number)" the line below already shows; other parentheses stay ("Full Art"). */
function shortName(name: string, setName: string | null, cardNumber: string | null): string {
  const n = setName ? name.replace(/\s+—\s+[^—]*$/, "") : name;
  return cardNumber ? n.replace(new RegExp(`\\s*\\(${cardNumber.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\)$`), "") : n;
}
const DEBOUNCE_MS = 200;

/**
 * Header search: as you type it suggests the closest products (thumbnail, name, set or category,
 * price); pick one to open it, or submit to see every offer at /mercado?q=… (shareable URL).
 */
export function SearchBar({ onSubmitted }: { onSubmitted?: () => void }) {
  const { t, pick, locale } = useI18n();
  const { catalog } = useCatalog();
  const storeSales = useStoreSales();
  const navigate = useNavigate();
  const { game } = useGame();
  const [params] = useSearchParams();
  const [value, setValue] = useState(params.get("q") ?? "");
  const [items, setItems] = useState<Product[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const id = useId();
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const focused = useRef(false);

  useEffect(() => {
    setValue(params.get("q") ?? "");
  }, [params]);

  // Suggestions for what's typed (only in the focused box: the header and the phone menu both have one).
  useEffect(() => {
    const q = value.trim();
    if (!focused.current || q.length < MIN_CHARS) {
      setItems([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      api
        .suggest(q, game, ctrl.signal)
        .then((r) => {
          // A slower answer for an older text never replaces the current one.
          if (ctrl.signal.aborted) return;
          // Only typing, focusing or the arrow keys open the list: a late answer after Enter,
          // Escape or leaving the box just updates what it would show.
          setItems(r.products);
          setActive(-1);
        })
        .catch(() => {});
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [value, game]);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  const go = (p: Product) => {
    close();
    navigate(`/producto/${p.slug}`);
    onSubmitted?.();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (open && active >= 0 && items[active]) return go(items[active]);
    close();
    const q = value.trim();
    navigate(q ? `/mercado?${new URLSearchParams({ q })}` : "/mercado");
    onSubmitted?.();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const shown = open && items.length > 0;
    if (e.key === "ArrowDown" && items.length) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % items.length);
    } else if (e.key === "ArrowUp" && shown) {
      e.preventDefault();
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (e.key === "Escape" && open) {
      // Close the list but keep the text (a search input would clear it).
      e.preventDefault();
      close();
    }
  };

  const shown = open && items.length > 0;
  const categoryName = (slug: string) => catalog.categories.find((c) => c.slug === slug);

  return (
    <form className="search" role="search" onSubmit={submit}>
      <label htmlFor={id} className="sr-only">
        {t("search.label")}
      </label>
      <input
        id={id}
        type="search"
        value={value}
        maxLength={MAX_QUERY_LENGTH}
        placeholder={t("search.placeholder")}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={listId}
        aria-activedescendant={shown && active >= 0 ? optionId(active) : undefined}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          focused.current = true;
          if (items.length) setOpen(true);
        }}
        onBlur={() => {
          focused.current = false;
          close();
        }}
        onKeyDown={onKeyDown}
      />
      <button type="submit" aria-label={t("search.submit")}>
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path d="m20 20-4.2-4.2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </button>
      <ul id={listId} role="listbox" aria-label={t("search.suggestions")} className="search-suggest" hidden={!shown}>
        {items.map((p, i) => {
          const name = pick(p.name);
          const category = categoryName(p.category);
          // A card: its set and number; anything else: what it is (and its set, if any).
          const detail = (p.category === "singles" ? [p.setName, p.cardNumber && `#${p.cardNumber}`] : [category && pick(category.name), p.setName]).filter(Boolean).join(" · ");
          const offered = p.market && p.market.count > 0 ? p.market : null;
          return (
            <li
              key={p.slug}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={i === active ? "search-option is-active" : "search-option"}
              // Keep focus in the box so the click lands before the list closes.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(p)}
            >
              <img
                src={imageUrl(p.imageKey)}
                alt=""
                width={40}
                height={40}
                loading="lazy"
                decoding="async"
                // No photo yet: keep the empty square instead of a broken image.
                onError={(e) => (e.currentTarget.style.visibility = "hidden")}
              />
              <span className="search-option-text">
                <span className="search-option-name">{shortName(name, p.setName, p.cardNumber)}</span>
                {detail && <span className="search-option-detail">{detail}</span>}
              </span>
              <span className="search-option-price">
                {storeSales && p.stock > 0 ? (
                  <Price price={p.price} priceArs={p.priceArs} />
                ) : offered ? (
                  t("search.fromSellers", { price: formatArs(offered.minPriceArs, locale) })
                ) : (
                  <span className="muted">{t("search.noStock")}</span>
                )}
              </span>
            </li>
          );
        })}
        <li
          id={optionId(items.length)}
          role="option"
          aria-selected={false}
          className="search-option search-all"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            close();
            navigate(`/mercado?${new URLSearchParams({ q: value.trim() })}`);
            onSubmitted?.();
          }}
        >
          {t("search.seeAll", { q: value.trim() })}
        </li>
      </ul>
    </form>
  );
}
